

import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { Settings, AdminUser, SvgIcon } from '../../types';
import { INITIAL_SETTINGS } from '../../constants';

interface DynamicIconProps {
    icon: SvgIcon;
    className?: string;
    color?: string;
}

const DynamicIcon: React.FC<DynamicIconProps> = ({ icon, className, color }) => (
    <svg 
        className={className} 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" height="24" 
        viewBox={icon?.viewBox || "0 0 24 24"} 
        fill="none" 
        stroke={color || "currentColor"} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        dangerouslySetInnerHTML={{ __html: icon?.svg || '' }}
    />
);

const ChevronDownIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
);

const MenuIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
);

const XIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);


interface AdminLayoutProps {
    onLogout: () => void;
    children: React.ReactNode;
    settings: Settings;
    currentUser: AdminUser;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ onLogout, children, settings, currentUser }) => {
    const [isDeveloperMenuOpen, setIsDeveloperMenuOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setSidebarOpen(false);
    }, [location]);
    
    const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
        `flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
            isActive
                ? 'bg-black/20 text-white'
                : 'text-gray-300 hover:bg-black/20 hover:text-white'
        }`;
    
    // Make icon rendering more robust by merging saved settings with defaults
    const icons = { ...INITIAL_SETTINGS.developer, ...(settings.developer || {}) };
    const isDeveloper = currentUser.role === 'developer';
    const iconColor = settings.theme.backendTheme.adminIconColor;

    return (
        <div className="flex h-screen bg-gray-100 overflow-hidden relative">
            {/* Mobile Sidebar Backdrop Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-300"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <aside 
                className={`fixed inset-y-0 left-0 z-40 w-64 text-white flex flex-col transition-transform duration-300 lg:static lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
                }`}
                style={{ backgroundColor: settings.theme.backendTheme.adminSidebarColor }}
            >
                <div className="h-16 flex items-center justify-between px-6 border-b border-white/10">
                    <a href="/" className="flex items-center gap-2 text-xl font-bold tracking-wider">
                        {settings.logoUrl ? (
                            <img src={settings.logoUrl} alt={settings.companyName} className="h-8 max-w-[100px] object-contain" />
                        ) : (
                            settings.companyName
                        )}
                    </a>
                    {/* Close button for mobile */}
                    <button 
                        onClick={() => setSidebarOpen(false)} 
                        className="lg:hidden p-1 text-gray-300 hover:text-white focus:outline-none"
                        aria-label="Close sidebar"
                    >
                        <XIcon className="h-6 w-6" />
                    </button>
                </div>
                <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto scrollbar-none">
                    <NavLink to="/admin/dashboard" className={navLinkClasses}>
                        <DynamicIcon icon={icons.projectsIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Projects
                    </NavLink>
                    <NavLink to="/admin/about" className={navLinkClasses}>
                        <DynamicIcon icon={icons.aboutIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        About Page
                    </NavLink>
                    <NavLink to="/admin/services" className={navLinkClasses}>
                        <DynamicIcon icon={icons.servicesIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Our Services
                    </NavLink>
                    <NavLink to="/admin/certificates" className={navLinkClasses}>
                        <DynamicIcon icon={icons.certificatesIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Certificates
                    </NavLink>
                    <NavLink to="/admin/testimonials" className={navLinkClasses}>
                        <DynamicIcon icon={icons.testimonialsIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Testimonials
                    </NavLink>
                    <NavLink to="/admin/pages" className={navLinkClasses}>
                        <DynamicIcon icon={icons.pagesIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Generic Pages
                    </NavLink>
                    {settings.enableJournal && (
                        <NavLink to="/admin/journal" className={navLinkClasses}>
                            <DynamicIcon icon={icons.journalIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                            Journal
                        </NavLink>
                    )}
                    <NavLink to="/admin/videos" className={navLinkClasses}>
                        <DynamicIcon icon={icons.videosIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Videos
                    </NavLink>
                     <NavLink to="/admin/team" className={navLinkClasses}>
                        <DynamicIcon icon={icons.teamIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Public Team
                    </NavLink>
                    <NavLink to="/admin/consultants" className={navLinkClasses}>
                        <DynamicIcon icon={icons.usersIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Consultants
                    </NavLink>
                    <NavLink to="/admin/associates" className={navLinkClasses}>
                        <DynamicIcon icon={icons.associatesIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Associates
                    </NavLink>
                    <NavLink to="/admin/settings" className={navLinkClasses}>
                        <DynamicIcon icon={icons.settingsIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Settings
                    </NavLink>
                    
                    {isDeveloper && (
                        <div className="pt-4 mt-4 border-t border-white/10">
                             <button onClick={() => setIsDeveloperMenuOpen(!isDeveloperMenuOpen)} className="w-full flex justify-between items-center px-4 py-3 text-sm font-medium rounded-md text-gray-300 hover:bg-black/20 hover:text-white">
                                  <div className="flex items-center">
                                     <DynamicIcon icon={icons.developerIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                                     Developer Tools
                                  </div>
                                  <ChevronDownIcon className={`h-5 w-5 transition-transform ${isDeveloperMenuOpen ? 'rotate-180' : ''}`} />
                             </button>
                             {isDeveloperMenuOpen && (
                                <div className="pl-4 mt-2 space-y-2">
                                    <NavLink to="/admin/users" className={navLinkClasses}>
                                        <DynamicIcon icon={icons.usersIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                                        User Management
                                    </NavLink>
                                    <NavLink to="/admin/invites" className={navLinkClasses}>
                                        <DynamicIcon icon={icons.invitesIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                                        User Invites
                                    </NavLink>
                                    <NavLink to="/admin/seeder" className={navLinkClasses}>
                                        <DynamicIcon icon={icons.databaseIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                                        Database Seeder
                                    </NavLink>
                                    <NavLink to="/admin/developer" className={navLinkClasses}>
                                        <DynamicIcon icon={icons.devSettingsIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                                        Developer Settings
                                    </NavLink>
                                </div>
                             )}
                        </div>
                    )}
                </nav>
                <div className="px-4 py-4 border-t border-white/10 flex-shrink-0">
                    <p className="px-4 text-xs text-gray-400 truncate" title={currentUser.email}>{currentUser.email}</p>
                    <button onClick={onLogout} className="w-full flex items-center px-4 py-3 text-sm font-medium rounded-md text-gray-300 hover:bg-black/20 hover:text-white mt-2">
                        <DynamicIcon icon={icons.logoutIcon} className="h-5 w-5 mr-3 flex-shrink-0" color={iconColor} />
                        Logout
                    </button>
                </div>
                <div className="text-center text-xs text-gray-400 pb-4 px-4 flex-shrink-0">
                    Developed by Servispro
                </div>
            </aside>
            <div className="flex-1 flex flex-col overflow-hidden w-full">
                {/* Admin Top Header on Mobile */}
                <header className="h-16 flex items-center justify-between px-6 bg-white border-b border-gray-200 lg:hidden flex-shrink-0">
                    <button 
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 -ml-2 text-gray-600 hover:text-gray-900 focus:outline-none"
                        aria-label="Open sidebar"
                    >
                        <MenuIcon className="h-6 w-6" />
                    </button>
                    <div className="text-lg font-bold text-gray-800 tracking-wide">
                        Admin Portal
                    </div>
                    <div className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center font-bold text-sm">
                        {currentUser.email.charAt(0).toUpperCase()}
                    </div>
                </header>

                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
                    <div className="container mx-auto px-6 py-8">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;